import React, { useEffect, useState } from 'react';
import GenericModal from '~/components/GenericModal/GenericModal';
import Spinner from '~/components/Spinner/Spinner';
import {
  ActivationCheck,
  ActivationChecksResponse,
  runHomebodyActivationChecks
} from '~/api/activationChecks';

interface IProps {
  buildingId: string;
  showModal: boolean;
  onChange: (show: boolean) => void;
  // Called after each run so the page can refresh anything the checks changed.
  onCompleted?: () => void;
}

const STATUS_ICON: Record<ActivationCheck['status'], string> = {
  ok: '✅',
  warn: '⚠️',
  fail: '❌'
};

const HomebodyActivationChecksModal = ({ buildingId, showModal, onChange, onCompleted }: IProps) => {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<ActivationChecksResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await runHomebodyActivationChecks(buildingId);
      setResult(data);
      onCompleted?.();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Checks failed to run.');
    } finally {
      setRunning(false);
    }
  };

  // Runs as soon as the dialog opens — the button is the trigger, no second click needed.
  useEffect(() => {
    if (showModal) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  const content = running ? (
    <div style={{ textAlign: 'center', padding: 24 }}>
      <Spinner />
      <p className='margin-top-16 bold'>Checking the building and setting up configs…</p>
      <p className='small-text'>Takes a few seconds — we also contact Entrata.</p>
    </div>
  ) : error ? (
    <p style={{ color: '#d32f2f' }}>{error}</p>
  ) : result ? (
    <div>
      <p className='bold' style={{ fontSize: 18, marginBottom: 12 }}>
        {result.verdict === 'ready'
          ? '✅ Ready — residents can use Cobu in this building.'
          : '❌ Not ready — fix the items marked ❌, then run again.'}
      </p>
      {result.checks.map((c) => (
        <div
          key={c.key}
          style={{ display: 'flex', gap: 10, padding: '8px 0', borderTop: '1px solid #eee' }}
        >
          <span style={{ width: 24 }}>{STATUS_ICON[c.status]}</span>
          <div>
            <p className='bold'>{c.label}</p>
            <p className='small-text'>{c.detail}</p>
          </div>
        </div>
      ))}
      {result.actions.length > 0 && (
        <div style={{ marginTop: 12, padding: 10, background: '#f5f7fa', borderRadius: 6 }}>
          <p className='small-text bold'>Done for you during this run</p>
          {result.actions.map((a) => (
            <p key={a} className='small-text'>
              • {a}
            </p>
          ))}
        </div>
      )}
    </div>
  ) : null;

  return (
    <GenericModal
      showModal={showModal}
      title='Homebody activation checks'
      content={content}
      actionText='Run again'
      actionFunction={run}
      actionButtonDisabled={running}
      cancelOverride='Close'
      onChange={onChange}
      width='640px'
    />
  );
};

export default HomebodyActivationChecksModal;
